/**
 * Arch Renderer
 *
 * Creates Three.js geometry for arch/Quonset-style hangar shells and end walls.
 * Uses custom BufferGeometry for the curved shell and ShapeGeometry for end walls.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { DoorSpec } from '@/lib/templates/types'
import type { ArchProfile } from '@/lib/templates/arch-hangar'
import { generateArchProfile, type ArchPoint } from '@/lib/templates/arch-hangar'
import { createWallMaterial, createDoorMaterial, applyShadowSettings } from '../utils/materials'

// ============================================================================
// TYPES
// ============================================================================

export interface ArchShellOptions {
  span_ft: number
  length_ft: number
  crown_height_ft: number
  knee_wall_height_ft: number
  arch_profile: ArchProfile
  arch_segments: number
  wall_thickness_ft: number
  color?: string
  enableShadows?: boolean
}

export interface ArchEndWallOptions {
  span_ft: number
  crown_height_ft: number
  knee_wall_height_ft: number
  arch_profile: ArchProfile
  arch_segments: number
  wall_thickness_ft: number
  doors?: DoorSpec[]
  color?: string
  enableShadows?: boolean
}

// ============================================================================
// ARCH SHELL (curved surface)
// ============================================================================

/**
 * Render the arch shell as a custom BufferGeometry.
 * The shell is a hollow curved surface extruded along the Z-axis.
 *
 * Vertex layout per cross-section slice (at z=0 and z=length):
 *   outer[0..N] and inner[0..N] where N = number of profile points
 *
 * Faces:
 *   - Outer surface: quads connecting consecutive outer vertices between z slices
 *   - Inner surface: quads connecting consecutive inner vertices (reversed normals)
 *   - Front/back edge strips: connect outer to inner at z=0 and z=length
 */
export function renderArchShell(options: ArchShellOptions): THREE.Mesh {
  const {
    span_ft,
    length_ft,
    crown_height_ft,
    knee_wall_height_ft,
    arch_profile,
    arch_segments,
    wall_thickness_ft,
    color,
    enableShadows = true,
  } = options

  // Generate the outer profile
  const outerProfile = generateArchProfile(
    span_ft,
    crown_height_ft,
    knee_wall_height_ft,
    arch_profile,
    arch_segments
  )

  // Generate inner profile (inset by wall thickness)
  const innerProfile = generateInnerProfile(outerProfile, wall_thickness_ft)

  const numPoints = outerProfile.length
  const geometry = buildShellGeometry(outerProfile, innerProfile, numPoints, length_ft)

  geometry.computeVertexNormals()

  const material = createWallMaterial({ color })
  const mesh = new THREE.Mesh(geometry, material)
  applyShadowSettings(mesh, enableShadows, enableShadows)
  mesh.name = 'archShell'

  return mesh
}

/**
 * Generate the inner profile by insetting each point along its local normal.
 */
function generateInnerProfile(
  outerProfile: ArchPoint[],
  thickness: number
): ArchPoint[] {
  const n = outerProfile.length
  const inner: ArchPoint[] = []

  for (let i = 0; i < n; i++) {
    // Compute approximate inward normal using adjacent points
    const prev = outerProfile[Math.max(0, i - 1)]
    const next = outerProfile[Math.min(n - 1, i + 1)]

    // Tangent along the profile
    const tx = next.x - prev.x
    const ty = next.y - prev.y
    const tLen = Math.sqrt(tx * tx + ty * ty)

    if (tLen < 0.001) {
      // Degenerate: just offset inward horizontally
      const sign = outerProfile[i].x < 0 ? 1 : -1
      inner.push({
        x: outerProfile[i].x + sign * thickness,
        y: outerProfile[i].y,
      })
      continue
    }

    // Inward normal: rotate tangent 90 degrees clockwise → (ty, -tx)
    // But we want inward (toward building center), so check direction
    let nx = ty / tLen
    let ny = -tx / tLen

    // Ensure normal points inward (toward center, roughly toward y-axis)
    // For arch profile, inward means the normal should point down/in
    // Simple heuristic: if the normal points away from center, flip it
    const centerX = 0
    const centerY = (outerProfile[0].y + outerProfile[Math.floor(n / 2)].y) / 2
    const toCenterX = centerX - outerProfile[i].x
    const toCenterY = centerY - outerProfile[i].y
    if (nx * toCenterX + ny * toCenterY < 0) {
      nx = -nx
      ny = -ny
    }

    inner.push({
      x: outerProfile[i].x + nx * thickness,
      y: outerProfile[i].y + ny * thickness,
    })
  }

  return inner
}

/**
 * Build the shell BufferGeometry from outer and inner profiles.
 */
function buildShellGeometry(
  outerProfile: ArchPoint[],
  innerProfile: ArchPoint[],
  numPoints: number,
  length_ft: number
): THREE.BufferGeometry {
  const halfLength = length_ft / 2

  // We need 2 z-slices (front z=+halfLength, back z=-halfLength)
  // Each slice has numPoints outer + numPoints inner vertices
  const totalVertices = numPoints * 2 * 2 // 2 profiles × 2 z-slices
  const positions = new Float32Array(totalVertices * 3)

  let vi = 0 // vertex index

  // Helper to push a vertex
  const pushVertex = (x: number, y: number, z: number) => {
    positions[vi * 3] = x * FEET_TO_METERS
    positions[vi * 3 + 1] = y * FEET_TO_METERS
    positions[vi * 3 + 2] = z * FEET_TO_METERS
    vi++
  }

  // Slice 0 (z = +halfLength, front)
  // outer[0..numPoints-1]
  for (let i = 0; i < numPoints; i++) {
    pushVertex(outerProfile[i].x, outerProfile[i].y, halfLength)
  }
  // inner[0..numPoints-1]
  for (let i = 0; i < numPoints; i++) {
    pushVertex(innerProfile[i].x, innerProfile[i].y, halfLength)
  }

  // Slice 1 (z = -halfLength, back)
  // outer[0..numPoints-1]
  for (let i = 0; i < numPoints; i++) {
    pushVertex(outerProfile[i].x, outerProfile[i].y, -halfLength)
  }
  // inner[0..numPoints-1]
  for (let i = 0; i < numPoints; i++) {
    pushVertex(innerProfile[i].x, innerProfile[i].y, -halfLength)
  }

  // Index offsets for the 4 vertex groups
  const outerFront = 0 // 0 .. numPoints-1
  const innerFront = numPoints // numPoints .. 2*numPoints-1
  const outerBack = numPoints * 2 // 2*numPoints .. 3*numPoints-1
  const innerBack = numPoints * 3 // 3*numPoints .. 4*numPoints-1

  const indices: number[] = []

  for (let i = 0; i < numPoints - 1; i++) {
    // Outer surface quad (front-left, back-left, back-right, front-right)
    // CCW winding for outward-facing normal
    indices.push(outerFront + i, outerBack + i, outerBack + i + 1)
    indices.push(outerFront + i, outerBack + i + 1, outerFront + i + 1)

    // Inner surface quad (reversed winding for inward-facing normal)
    indices.push(innerFront + i, innerFront + i + 1, innerBack + i + 1)
    indices.push(innerFront + i, innerBack + i + 1, innerBack + i)

    // Front edge strip (z = +halfLength): outer to inner
    indices.push(outerFront + i, outerFront + i + 1, innerFront + i + 1)
    indices.push(outerFront + i, innerFront + i + 1, innerFront + i)

    // Back edge strip (z = -halfLength): outer to inner (reversed)
    indices.push(outerBack + i, innerBack + i, innerBack + i + 1)
    indices.push(outerBack + i, innerBack + i + 1, outerBack + i + 1)
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geometry.setIndex(indices)

  return geometry
}

// ============================================================================
// END WALLS (flat walls shaped to match arch profile, with door cutouts)
// ============================================================================

/**
 * Render an end wall at the front or back of the arch hangar.
 * The wall is shaped to match the arch profile outline.
 */
export function renderArchEndWall(
  side: 'front' | 'back',
  options: ArchEndWallOptions
): THREE.Group {
  const {
    span_ft,
    crown_height_ft,
    knee_wall_height_ft,
    arch_profile,
    arch_segments,
    wall_thickness_ft,
    doors,
    color,
    enableShadows = true,
  } = options

  const group = new THREE.Group()
  group.name = `endWall-${side}`

  // Generate the arch profile for the wall outline
  const profile = generateArchProfile(
    span_ft,
    crown_height_ft,
    knee_wall_height_ft,
    arch_profile,
    arch_segments
  )

  // Create a THREE.Shape from the profile
  const shape = new THREE.Shape()
  shape.moveTo(profile[0].x * FEET_TO_METERS, profile[0].y * FEET_TO_METERS)
  for (let i = 1; i < profile.length; i++) {
    shape.lineTo(profile[i].x * FEET_TO_METERS, profile[i].y * FEET_TO_METERS)
  }
  shape.closePath()

  // Cut door holes
  if (doors && doors.length > 0) {
    for (const door of doors) {
      const doorLeft = (door.offset_ft - span_ft / 2) * FEET_TO_METERS
      const doorRight = doorLeft + door.width_ft * FEET_TO_METERS
      const doorTop = door.height_ft * FEET_TO_METERS

      const hole = new THREE.Path()
      hole.moveTo(doorLeft, 0)
      hole.lineTo(doorRight, 0)
      hole.lineTo(doorRight, doorTop)
      hole.lineTo(doorLeft, doorTop)
      hole.closePath()
      shape.holes.push(hole)
    }
  }

  // Create end wall mesh
  const wallGeometry = new THREE.ShapeGeometry(shape)
  const material = createWallMaterial({ color })
  const wallMesh = new THREE.Mesh(wallGeometry, material)
  applyShadowSettings(wallMesh, enableShadows, enableShadows)
  wallMesh.name = `wall-${side}`

  // Position and orient the wall
  const halfLength = (options as unknown as { length_ft?: number }).length_ft
    ? ((options as unknown as { length_ft: number }).length_ft / 2) * FEET_TO_METERS
    : 0

  // Calculate z position from the containing orchestrator's context
  // End walls need to know the building length, which we get via the options extension
  const zPos = side === 'front'
    ? halfLength + (wall_thickness_ft / 2) * FEET_TO_METERS
    : -(halfLength + (wall_thickness_ft / 2) * FEET_TO_METERS)

  wallMesh.position.z = zPos

  // Back wall faces opposite direction
  if (side === 'back') {
    wallMesh.rotation.y = Math.PI
  }

  group.add(wallMesh)

  // Add door visualizations
  if (doors && doors.length > 0) {
    for (const door of doors) {
      const doorMaterial = createDoorMaterial(door.type)
      const doorWidth = door.width_ft * FEET_TO_METERS
      const doorHeight = door.height_ft * FEET_TO_METERS

      const doorMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(doorWidth, doorHeight),
        doorMaterial
      )

      const doorCenterX = (door.offset_ft + door.width_ft / 2 - span_ft / 2) * FEET_TO_METERS
      doorMesh.position.set(
        doorCenterX,
        doorHeight / 2,
        zPos + (side === 'front' ? 0.01 : -0.01)
      )

      if (side === 'back') {
        doorMesh.rotation.y = Math.PI
      }

      doorMesh.name = `door-${door.type}`
      group.add(doorMesh)
    }
  }

  return group
}

/**
 * Render an end wall with explicit length (avoids the options hack above).
 */
export function renderArchEndWallWithLength(
  side: 'front' | 'back',
  length_ft: number,
  options: ArchEndWallOptions
): THREE.Group {
  const {
    span_ft,
    crown_height_ft,
    knee_wall_height_ft,
    arch_profile,
    arch_segments,
        doors,
    color,
    enableShadows = true,
  } = options

  const group = new THREE.Group()
  group.name = `endWall-${side}`

  const profile = generateArchProfile(
    span_ft,
    crown_height_ft,
    knee_wall_height_ft,
    arch_profile,
    arch_segments
  )

  // Create arch-shaped wall
  const shape = new THREE.Shape()
  shape.moveTo(profile[0].x * FEET_TO_METERS, profile[0].y * FEET_TO_METERS)
  for (let i = 1; i < profile.length; i++) {
    shape.lineTo(profile[i].x * FEET_TO_METERS, profile[i].y * FEET_TO_METERS)
  }
  shape.closePath()

  // Cut door holes
  if (doors && doors.length > 0) {
    for (const door of doors) {
      const doorLeft = (door.offset_ft - span_ft / 2) * FEET_TO_METERS
      const doorRight = doorLeft + door.width_ft * FEET_TO_METERS
      const doorTop = door.height_ft * FEET_TO_METERS

      const hole = new THREE.Path()
      hole.moveTo(doorLeft, 0)
      hole.lineTo(doorRight, 0)
      hole.lineTo(doorRight, doorTop)
      hole.lineTo(doorLeft, doorTop)
      hole.closePath()
      shape.holes.push(hole)
    }
  }

  const wallGeometry = new THREE.ShapeGeometry(shape)
  const material = createWallMaterial({ color })
  const wallMesh = new THREE.Mesh(wallGeometry, material)
  applyShadowSettings(wallMesh, enableShadows, enableShadows)
  wallMesh.name = `wall-${side}`

  const halfLength = (length_ft / 2) * FEET_TO_METERS

  if (side === 'front') {
    wallMesh.position.z = halfLength
  } else {
    wallMesh.position.z = -halfLength
    wallMesh.rotation.y = Math.PI
  }

  group.add(wallMesh)

  // Door visualizations
  if (doors && doors.length > 0) {
    for (const door of doors) {
      const doorMaterial = createDoorMaterial(door.type)
      const doorWidth = door.width_ft * FEET_TO_METERS
      const doorHeight = door.height_ft * FEET_TO_METERS

      const doorMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(doorWidth, doorHeight),
        doorMaterial
      )

      const doorCenterX = (door.offset_ft + door.width_ft / 2 - span_ft / 2) * FEET_TO_METERS
      const zPos = side === 'front' ? halfLength : -halfLength

      doorMesh.position.set(
        doorCenterX,
        doorHeight / 2,
        zPos + (side === 'front' ? 0.01 : -0.01)
      )

      if (side === 'back') {
        doorMesh.rotation.y = Math.PI
      }

      doorMesh.name = `door-${door.type}`
      group.add(doorMesh)
    }
  }

  return group
}
